import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Download, Stethoscope } from "lucide-react";

const VIEWS = [
  ["sources", "By source", BarChart3],
  ["partners", "By doctor partner", Stethoscope],
];

const LABELS = {
  dr_qr: "Doctor QR",
  youtube: "YouTube",
  instagram: "Instagram",
  facebook: "Facebook",
  website_bio: "Website bio",
  direct: "Direct",
};

export const sourceLabel = (src) =>
  LABELS[src] || String(src || "direct").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const monthName = (month) => {
  if (month === "all") return "All time";
  const [y, m] = month.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

const money = (value) => `₹${Number(value || 0).toFixed(0)}`;

const Metric = ({ name, value, testid }) => (
  <div className="rounded-xl border border-border p-4" data-testid={testid}>
    <div className="overline">{name}</div>
    <div className="font-display text-2xl mt-1">{value}</div>
  </div>
);

/**
 * Admin → Reports: what every channel and every referring doctor produced in a month,
 * straight from the `src` / `ref` stamped on each order, appointment and prescription.
 */
export const ReportsTab = () => {
  const [view, setView] = useState("sources");
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/reports/${view}`, { params: month ? { month } : {} })
      .then(({ data: d }) => {
        setData(d);
        if (!month) setMonth(d.month);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [view, month]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const downloadCsv = async () => {
    try {
      const { data: blob } = await api.get(`/admin/reports/${view}.csv`, {
        params: { month: month || "all" }, responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${view}-${month || "all-time"}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="border-border" data-testid="reports-tab">
      <CardContent className="p-6 lg:p-8">
        <h3 className="font-display text-2xl mb-1">Reports</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Counted from where each patient actually came from — kept for 30 days from their first visit.
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {VIEWS.map(([value, label, Icon]) => (
            <Button
              key={value}
              variant={view === value ? "default" : "outline"}
              className="btn-pill h-10"
              onClick={() => setView(value)}
              data-testid={`reports-view-${value}`}
            >
              <Icon size={13} className="mr-1.5" /> {label}
            </Button>
          ))}
          <Select value={month || "all"} onValueChange={setMonth}>
            <SelectTrigger className="w-52 h-10" data-testid="reports-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              {(data?.months || []).map((m) => <SelectItem key={m} value={m}>{monthName(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="btn-pill h-10" onClick={downloadCsv} data-testid="reports-csv">
            <Download size={13} className="mr-1.5" /> CSV
          </Button>
        </div>

        {view === "sources" ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <Metric name="Orders paid" value={totals.paid_orders || 0} testid="reports-total-orders" />
              <Metric name="Revenue" value={money(totals.revenue)} testid="reports-total-revenue" />
              <Metric name="Appointments" value={totals.appointments || 0} testid="reports-total-appointments" />
              <Metric name="Prescriptions" value={totals.prescriptions || 0} testid="reports-total-rx" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-sources-table">
                <thead className="text-left">
                  <tr className="border-b border-border">
                    {["Source", "Orders", "Paid", "Revenue", "Appointments", "Prescriptions"].map((h) => (
                      <th key={h} className="overline py-2 pr-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.src} className="border-b border-border/60" data-testid={`reports-row-${row.src}`}>
                      <td className="py-2.5 pr-4 font-medium">{sourceLabel(row.src)}</td>
                      <td className="py-2.5 pr-4">{row.orders}</td>
                      <td className="py-2.5 pr-4">{row.paid_orders}</td>
                      <td className="py-2.5 pr-4">{money(row.revenue)}</td>
                      <td className="py-2.5 pr-4">{row.appointments}</td>
                      <td className="py-2.5 pr-4">{row.prescriptions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <Metric name="QR scans" value={totals.scans || 0} testid="reports-partner-scans" />
              <Metric name="Referrals" value={totals.referrals || 0} testid="reports-partner-referrals" />
              <Metric name="Orders paid" value={totals.paid_orders || 0} testid="reports-partner-orders" />
              <Metric name="Revenue" value={money(totals.revenue)} testid="reports-partner-revenue" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-partners-table">
                <thead className="text-left">
                  <tr className="border-b border-border">
                    {["Code", "Doctor", "Scans", "Referrals", "Rx", "Appointments", "Orders", "Revenue"].map((h) => (
                      <th key={h} className="overline py-2 pr-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.code} className="border-b border-border/60" data-testid={`reports-partner-${row.code}`}>
                      <td className="py-2.5 pr-4 font-mono text-xs">
                        {row.code}
                        {row.is_partner && <Badge variant="outline" className="ml-2 text-[10px]">partner</Badge>}
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="font-medium">{row.doctor_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{row.clinic}</div>
                      </td>
                      <td className="py-2.5 pr-4">{row.scans}</td>
                      <td className="py-2.5 pr-4">{row.referrals}</td>
                      <td className="py-2.5 pr-4">{row.prescriptions}</td>
                      <td className="py-2.5 pr-4">{row.appointments}</td>
                      <td className="py-2.5 pr-4">{row.paid_orders}</td>
                      <td className="py-2.5 pr-4">{money(row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground mt-4" data-testid="reports-empty">
            Nothing recorded for {data?.month_label || "this period"} yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportsTab;
